import React, { useEffect, useMemo, useState } from 'react';

// Utils
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import useNotification from '../../utils/hooks/useNotification';
import withAuth from '../../utils/hocs/withAuth';
import withPermission from '../../utils/hocs/withPermission';
import SCOPE from '../../utils/gate/scope';
import withSidebar from '../../utils/hocs/withSidebar';
import packageSchema from '../../validations/packageSchema';
import useForm from '../../utils/hooks/useForm';

// Services
import usePackageService from '../../services/packageService';
import useAccommodationService from '../../services/accommodationService';
import useCampService from '../../services/campService';
import useExtraService from '../../services/extraService';

// Components
import PageWrapper from '../../components/layout/PageWrapper/PageWrapper';
import PackageConfiguration from '../../components/views/establishment/Packages/PackageConfiguration';
import Button from '../../components/atoms/Button/Button';
import SkeletonLoader from '../../components/molecules/SkeletonLoader/SkeletonLoader';

function PackageEdition() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { displayNotification } = useNotification();
  const { handleScrollToError } = useForm();

  // URL query params
  const [searchParams] = useSearchParams();
  const packageId = useMemo(() => searchParams.get('id'), [searchParams]);
  useEffect(() => { if (!packageId) navigate('/packages'); }, []);

  const [packageTitle, setPackageTitle] = useState(null);
  const [extrasWithQuantities, setExtrasWithQuantities] = useState([]);
  const [extrasTouched, setExtrasTouched] = useState(false);

  const [initialValues, setInitialValues] = useState({
    name: '',
    isActive: true,
    campId: null,
    accommodationIds: [],
    isAccommodationMandatory: false,
  });

  const { getPackage, putPackage } = usePackageService();
  const { getAllCamps } = useCampService();
  const { getAllExtras } = useExtraService();
  const { getAllAccommodations } = useAccommodationService();

  const { data: camps, isFetching: isFetchingCamps } = useQuery('getAllCamps', () => getAllCamps());
  const { data: extras, isFetching: isFetchingExtras } = useQuery(
    'getAllExtras',
    () => getAllExtras(),
    {
      onSuccess: (data) => {
        if (data?.data?.extras?.length) {
          setExtrasWithQuantities(data.data.extras.map((extra) => ({
            id: extra.id,
            label: extra.name,
            quantity: 0,
          })));
        }
      },
    },
  );
  const { data: accommodations, isFetching: isFetchingAccommodations } = useQuery(
    'getAllAccommodations',
    () => getAllAccommodations(),
  );

  const setFormattedPackage = (initialPackage) => {
    const packCamp = camps?.data?.camps?.find((c) => c.id === initialPackage.campId);
    const packAccIds = accommodations?.data?.accommodations
      ?.filter((a) => initialPackage.accommodationIds?.includes(a.id));

    setInitialValues({
      name: initialPackage.name || '',
      isActive: initialPackage.isActive || false,
      campId: packCamp ? { label: packCamp.name, value: packCamp.id } : null,
      accommodationIds: packAccIds?.length ? packAccIds.map((acc) => ({ label: acc.name, value: acc.id })) : [],
      isAccommodationMandatory: initialPackage.isAccommodationMandatory || false,
    });
  };

  const { isFetching } = useQuery(
    'getPackage',
    () => getPackage(packageId),
    {
      enabled: !isNil(camps) && !isNil(accommodations) && !isNil(extras),
      onSuccess: (data) => {
        const initialPackage = data?.data;
        if (initialPackage) {
          setPackageTitle(initialPackage.name);
          setFormattedPackage(initialPackage);

          if (initialPackage.extras?.length) {
            setExtrasWithQuantities(
              (state) => state.map((item) => (initialPackage.extras.some((ex) => ex.extraId === item.id)
                ? {
                  ...item,
                  quantity: initialPackage.extras.find((ex) => ex.extraId === item.id)?.maxQuantity || 0,
                }
                : item)),
            );
          }
        }
      },
    },
  );

  const { isLoading: isPutPackageLoading, mutate: putPackageMutate } = useMutation(
    (values) => putPackage({ packageId, pack: values }),
    {
      onSuccess: (data) => {
        displayNotification({
          message: t('common.successfullySaved'),
        });

        const initialPackage = data?.data;
        if (initialPackage) {
          setFormattedPackage(initialPackage);
          setPackageTitle(initialPackage.name);
          setFormattedPackage(initialPackage);
        }
        setExtrasTouched(false);
      },

      onError: (error) => {
        displayNotification({
          message: t(error?.response?.data?.code
            ? `code_errors.${error.response.data.code}`
            : 'common.saveError'),
          color: 'error',
        });
      },
    },
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values) {
        putPackageMutate({
          name: values.name,
          isActive: values.isActive,
          campId: values.campId?.value,
          extras: extrasWithQuantities
            ?.filter((extra) => extra.quantity > 0)
            ?.map((extra) => ({ extraId: extra.id, maxQuantity: extra.quantity })) || [],
          accommodationIds: values.accommodationIds?.map((acc) => acc.value),
          isAccommodationMandatory: values.isAccommodationMandatory,
        });
      }
    },
    validationSchema: packageSchema,
    validateOnBlur: true,
  });

  return (
    <PageWrapper
      title={packageTitle}
      hasWhiteWrapper
      breadcrumb={[
        { label: t('packages.title'), href: '/packages' },
      ]}
    >
      {isFetching ? (
        <>
          <div className="input_max">
            <SkeletonLoader className="m-b-25" height={30} />
            <SkeletonLoader className="m-b-70" height={100} />

            <SkeletonLoader className="m-b-25" height={30} />
            <SkeletonLoader className="m-b-70" height={50} />
          </div>

          <div className="input_max">
            <SkeletonLoader className="m-b-25" height={30} />
          </div>
          <SkeletonLoader className="m-b-70" height={400} />

          <div className="input_max">
            <SkeletonLoader className="m-b-25" height={30} />
            <SkeletonLoader className="m-b-30" height={20} />
            <SkeletonLoader className="m-b-30" height={50} />
          </div>
        </>
      ) : (
        <>
          <PackageConfiguration
            formik={{
              values: formik.values,
              errors: formik.errors,
              touched: formik.touched,
              handleChange: formik.handleChange,
              handleBlur: formik.handleBlur,
              setFieldValue: formik.setFieldValue,
              setFieldTouched: formik.setFieldTouched,
            }}
            isDataLoading={isFetchingCamps || isFetchingAccommodations || isFetchingExtras}
            camps={camps?.data?.camps || []}
            accommodations={accommodations?.data?.accommodations || []}
            extrasWithQuantities={extrasWithQuantities}
            setExtrasWithQuantities={setExtrasWithQuantities}
            setExtrasTouched={setExtrasTouched}
          />

          <div className="d-flex a-center j-end sticky-actions p-r-30 mobile-p-r-0">
            <Button
              full={isMobile}
              onClick={() => {
                formik.submitForm().then(() => handleScrollToError(formik.errors));
              }}
              isLoading={isPutPackageLoading}
              disabled={!formik.dirty && !extrasTouched}
            >
              {t('common.saveModifications')}
            </Button>
          </div>
        </>
      )}
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(PackageEdition),
    {
      scopes: [SCOPE.create_ownEst_package, SCOPE.update_ownEst_package],
    },
  ),
);
